import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, ConfirmDialog, Drawer, EmptyState, Input, Spinner, Switch, Textarea, useToast } from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { recipeCategoriesApi } from './recipesApi';

const EMPTY = { name: '', description: '', sortOrder: 0, isActive: true };

export default function RecipeCategoriesTab({ canManage }) {
  const toast = useToast();
  const t = useT();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCategories((await recipeCategoriesApi.list()) || []);
    } catch (err) {
      toast.error(t('Could not load categories'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDrawerOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name ?? '',
      description: c.description ?? '',
      sortOrder: c.sortOrder ?? 0,
      isActive: c.isActive ?? true,
    });
    setDrawerOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        sortOrder: Number(form.sortOrder) || 0,
        isActive: form.isActive,
      };
      if (editing) await recipeCategoriesApi.update(editing.id, { id: editing.id, ...payload });
      else await recipeCategoriesApi.create(payload);
      toast.success(editing ? t('Category updated') : t('Category created'));
      setDrawerOpen(false);
      await load();
    } catch (err) {
      toast.error(t('Could not save category'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await recipeCategoriesApi.remove(deleteTarget.id);
      toast.success(t('Category deleted'));
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(t('Could not delete category'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-12)' }}><Spinner size={24} /></div>;
  }

  return (
    <>
      <div className="pp-toolbar">
        <div className="pp-toolbar__spacer" />
        {canManage && (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>
            {t('New category')}
          </Button>
        )}
      </div>

      {categories.length === 0 ? (
        <EmptyState
          icon={<Icon.Folders size={28} />}
          title={t('No menu categories yet')}
          description={t('Group recipes into menu sections (e.g. Appetizers, Mains, Desserts) for organisation and reporting.')}
          action={canManage ? <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>{t('New category')}</Button> : null}
        />
      ) : (
        <div className="cat-list">
          {categories.map((c) => (
            <div className="cat-row" key={c.id}>
              <span className="cat-row__icon"><Icon.Folder size={18} /></span>
              <div className="cat-row__main">
                <span className="cat-row__name">
                  {c.name}
                  {!c.isActive && <Badge tone="neutral">{t('Inactive')}</Badge>}
                </span>
                {c.description && <span className="cat-row__meta">{c.description}</span>}
              </div>
              {canManage && (
                <div className="cat-row__actions">
                  <IconButton aria-label={t('Edit category')} variant="ghost" size="icon-sm" onClick={() => openEdit(c)}>
                    <Icon.Pencil size={15} />
                  </IconButton>
                  <IconButton aria-label={t('Delete category')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(c)}>
                    <Icon.Trash size={15} />
                  </IconButton>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Drawer
        open={drawerOpen}
        onClose={() => (submitting ? null : setDrawerOpen(false))}
        title={editing ? t('Edit category') : t('New category')}
        description={t('Menu sections used to group recipes and menu items.')}
        size="md"
      >
        <form className="pp-form" onSubmit={save}>
          <Input label={t('Name')} placeholder={t('e.g. Appetizers')} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} autoFocus />
          <Input label={t('Sort order')} type="number" value={form.sortOrder} onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))} hint={t('Lower numbers appear first')} />
          <Textarea label={t('Description')} rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          <Switch checked={form.isActive} onChange={(v) => setForm((f) => ({ ...f, isActive: v }))} label={t('Active')} />
          <div className="pp-form__actions">
            <Button type="button" variant="ghost" onClick={() => setDrawerOpen(false)} disabled={submitting}>{t('Cancel')}</Button>
            <Button type="submit" variant="primary" loading={submitting}>{editing ? t('Save changes') : t('Create category')}</Button>
          </div>
        </form>
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('Delete category')}
        description={deleteTarget ? t('Delete "{name}"? Recipes using it must be reassigned first.', { name: deleteTarget.name }) : ''}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={deleting}
        onConfirm={doDelete}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
      />
    </>
  );
}
